<template>
    <div class="bg-white w-100 rounded-4 border config-section dashboard-kb-panel">
        <h2
            class="px-4 py-3 section-heading d-flex justify-content-between align-items-center"
            @click="expanded = !expanded"
        >
            <span>Dashboard Knowledge</span>
            <span class="toggle-icon">{{ expanded ? '−' : '+' }}</span>
        </h2>

        <div class="p-4" v-show="expanded">
            <div class="d-flex justify-content-between align-items-start gap-3 mb-3 flex-wrap">
                <p class="text-muted mb-0 panel-intro">
                    Every Home dashboard is added to the chatbot automatically when it's created or
                    changed — its tile names, a generated summary of what members can do there, and the
                    content behind any links or PDFs on its tiles. Members only ever see knowledge from
                    the dashboard they're assigned to. Review and correct anything that doesn't read
                    well below.
                </p>
                <!-- One-click population for companies whose dashboards predate this
                     feature (or a full refresh) — the UI equivalent of the backfill
                     script, so no CLI is needed. -->
                <button
                    class="btn btn-outline-primary btn-sm flex-shrink-0"
                    :disabled="ingestingAll"
                    title="Fetch and index every Home dashboard for this company"
                    @click="reingestAll"
                >
                    <span v-if="ingestingAll" class="spinner-border spinner-border-sm me-2" />
                    {{ ingestingAll ? 'Ingesting…' : 'Ingest All Dashboards' }}
                </button>
            </div>

            <!-- Search — only when there's enough to search through -->
            <div v-if="total > SEARCH_THRESHOLD || search" class="kb-search-row mb-3">
                <div class="kb-search-input-wrapper">
                    <input
                        type="text"
                        class="form-control kb-search-input"
                        v-model="search"
                        placeholder="Search dashboards by name…"
                        @input="onSearchInput"
                    />
                    <button
                        v-if="search"
                        class="kb-search-clear"
                        @click="clearSearch"
                        title="Clear search"
                    >×</button>
                </div>
                <span class="kb-search-count text-muted small">
                    {{ rows.length }} of {{ total }} shown
                </span>
            </div>

            <div v-if="listLoading" class="text-muted small mb-3">Loading dashboards…</div>

            <div v-else-if="notMigrated" class="alert alert-warning py-2 mb-3">
                <strong>Not set up yet.</strong>
                Dashboard knowledge storage is missing from the database. Run the pending
                migrations (<code>sql_scripts/1.85.0.sql</code>), then reload this page.
                Dashboards you create meanwhile will be picked up by the backfill script.
            </div>

            <div v-else-if="listError" class="alert alert-danger py-2">{{ listError }}</div>

            <div v-else-if="!rows.length && !search && !notMigrated" class="text-muted small mb-3">
                No dashboard knowledge yet. It's added automatically when a Home dashboard is
                created or saved — or use
                <strong>Re-ingest</strong> on an existing dashboard from the Dashboard Builder.
            </div>

            <div v-else-if="!rows.length" class="text-muted small mb-3 text-center py-3">
                No dashboards match "<strong>{{ search }}</strong>".
            </div>

            <!-- One card per dashboard -->
            <div class="accordion" v-if="rows.length">
                <div
                    v-for="row in rows"
                    :key="row.dashboardId"
                    class="accordion-item mb-2 border rounded"
                >
                    <button
                        class="accordion-header"
                        @click="toggleDashboard(row)"
                        :aria-expanded="openDashboards[row.dashboardId] ? 'true' : 'false'"
                    >
                        <span class="accordion-toggle">{{ openDashboards[row.dashboardId] ? '−' : '+' }}</span>
                        <span class="accordion-title">
                            <strong>{{ row.dashboardName || '(unnamed dashboard)' }}</strong>
                            <!-- Features at a glance. The whole point of this panel is
                                 answering "what does the chatbot know about this
                                 dashboard?", and the feature list is half that answer —
                                 requiring a click to see it defeats the summary. -->
                            <span class="module-pills" v-if="row.modules && row.modules.length">
                                <span
                                    v-for="m in visibleModules(row.modules)"
                                    :key="m"
                                    class="module-pill"
                                >{{ m }}</span>
                                <span
                                    v-if="row.modules.length > MODULE_PILL_LIMIT"
                                    class="module-pill module-pill-more"
                                    :title="row.modules.join(', ')"
                                >+{{ row.modules.length - MODULE_PILL_LIMIT }} more</span>
                            </span>
                            <span v-else class="accordion-url small text-muted d-block">
                                No features on this dashboard yet
                            </span>
                            <span class="accordion-url small text-muted d-block">
                                {{ row.linksIngested }} of {{ row.linksTotal }} link{{ row.linksTotal === 1 ? '' : 's' }} indexed
                                <template v-if="row.linksSkipped">· {{ row.linksSkipped }} skipped</template>
                                <template v-if="row.lastIngestedAt">
                                    · updated {{ formatDate(row.lastIngestedAt) }}
                                </template>
                            </span>
                        </span>
                        <span class="chunks-pill" v-if="row.chunksIndexed">
                            {{ row.chunksIndexed }} chunks
                        </span>
                        <span class="stale-pill" v-if="isStale(row)" title="Linked pages were read a long time ago and may have changed">
                            may be out of date
                        </span>
                    </button>

                    <div class="accordion-body p-3" v-show="openDashboards[row.dashboardId]">
                        <div v-if="row._loading" class="text-muted small">Loading dashboard knowledge…</div>
                        <div v-else-if="row._error" class="alert alert-danger py-2 mb-0">{{ row._error }}</div>

                        <template v-else-if="row._detail">
                            <!-- Generated description — editable -->
                            <div class="form-row">
                                <label class="form-label">
                                    What members can do here
                                    <small class="text-muted d-block fw-normal">
                                        Written automatically from this dashboard's tiles and linked
                                        resources. Edits are replaced the next time the dashboard
                                        itself changes.
                                    </small>
                                </label>
                                <textarea
                                    class="form-control"
                                    rows="4"
                                    v-model="row._detail.description"
                                ></textarea>
                            </div>

                            <!-- Tile tree — derived, read-only -->
                            <div class="form-row">
                                <label class="form-label">
                                    Home screen tiles
                                    <small class="text-muted d-block fw-normal">
                                        Read-only — this mirrors the dashboard. Change it in the
                                        Dashboard Builder.
                                    </small>
                                </label>
                                <pre class="tile-tree">{{ row._detail.tileTree || '(no tiles yet)' }}</pre>
                            </div>

                            <div class="d-flex justify-content-between align-items-center mt-3 mb-4">
                                <button
                                    class="btn btn-outline-secondary btn-sm"
                                    :disabled="row._reingesting"
                                    @click="reingest(row)"
                                    title="Re-read this dashboard's tiles and linked pages"
                                >
                                    <span v-if="row._reingesting" class="spinner-border spinner-border-sm me-2" />
                                    {{ row._reingesting ? 'Re-reading…' : 'Re-ingest' }}
                                </button>
                                <button
                                    class="btn btn-primary btn-sm"
                                    :disabled="row._savingDescription"
                                    @click="saveDescription(row)"
                                >{{ row._savingDescription ? 'Saving…' : 'Save Description' }}</button>
                            </div>

                            <!-- Modules, each with the resources linked from its tiles.
                                 Grouped by module rather than shown as one flat list,
                                 because that's how the chatbot scopes retrieval: a member
                                 without a module never sees its links. -->
                            <h6 class="section-subheading">
                                Features and their linked resources
                                <span class="text-muted fw-normal small">
                                    ({{ row._detail.modules.length }} feature{{ row._detail.modules.length === 1 ? '' : 's' }})
                                </span>
                            </h6>

                            <p v-if="!moduleGroups(row).length" class="text-muted small">
                                This dashboard has no features yet.
                            </p>

                            <div
                                v-for="group in moduleGroups(row)"
                                :key="group.module"
                                class="module-group border rounded mb-3"
                            >
                                <div class="module-group-header">
                                    <span class="module-name">{{ group.module }}</span>
                                    <span class="module-counts text-muted small">
                                        <template v-if="group.indexed.length">
                                            {{ group.indexed.length }} resource{{ group.indexed.length === 1 ? '' : 's' }} indexed
                                        </template>
                                        <template v-else>no linked resources</template>
                                        <template v-if="group.skipped.length">
                                            · {{ group.skipped.length }} skipped
                                        </template>
                                    </span>
                                </div>

                                <div class="module-group-body">
                                    <p v-if="!group.indexed.length && !group.skipped.length" class="text-muted small mb-0">
                                        Nothing linked from this feature's tiles. The chatbot can still
                                        explain how to use it from its built-in feature guides.
                                    </p>

                                    <!-- Indexed resources — editable -->
                                    <div
                                        v-for="page in group.indexed"
                                        :key="page.id"
                                        class="page-item border rounded mb-2"
                                    >
                                        <button class="page-header" @click="togglePage(page.id)">
                                            <span class="accordion-toggle">{{ openPages[page.id] ? '−' : '+' }}</span>
                                            <span class="accordion-title">
                                                <strong>{{ page.title || page.url }}</strong>
                                                <span class="accordion-url small text-muted d-block">
                                                    {{ page.linkPath }}
                                                </span>
                                            </span>
                                            <span class="kind-pill" v-if="page.sourceKind">
                                                {{ page.sourceKind === 'pdf' ? 'PDF' : page.sourceKind }}
                                            </span>
                                            <span class="chunks-pill" v-if="page.chunksIndexed">
                                                {{ page.chunksIndexed }} chunks
                                            </span>
                                            <span
                                                class="stale-pill"
                                                v-if="page.skipReason && page.chunksIndexed"
                                                :title="page.skipReason"
                                            >refresh failed</span>
                                        </button>

                                        <div class="p-3" v-show="openPages[page.id]">
                                            <!-- Content is live, but the most recent attempt to
                                                 re-read the source failed. Say both, so nobody
                                                 assumes it's broken or assumes it's current. -->
                                            <div
                                                v-if="page.skipReason && page.chunksIndexed"
                                                class="alert alert-warning py-2 small"
                                            >
                                                <strong>Last refresh failed:</strong> {{ page.skipReason }}<br>
                                                The chatbot is still answering from the previous copy
                                                shown below. Fix the link or use <strong>Re-ingest</strong>
                                                to try again.
                                            </div>

                                            <div
                                                v-if="page.sharedWith && page.sharedWith.length"
                                                class="alert alert-warning py-2 small"
                                            >
                                                This resource is also linked from
                                                {{ page.sharedWith.length }} other dashboard{{ page.sharedWith.length === 1 ? '' : 's' }}.
                                                Editing it here changes it for those too.
                                            </div>

                                            <div class="form-row">
                                                <label class="form-label">Title</label>
                                                <input class="form-control" v-model="page.title" />
                                            </div>
                                            <div class="form-row">
                                                <label class="form-label">URL</label>
                                                <input class="form-control" :value="page.url" readonly />
                                            </div>
                                            <div class="form-row">
                                                <label class="form-label">Topic</label>
                                                <input class="form-control" v-model="page.topic" />
                                            </div>
                                            <div class="form-row">
                                                <label class="form-label">Summary</label>
                                                <textarea class="form-control" rows="3" v-model="page.summary"></textarea>
                                            </div>
                                            <div class="form-row">
                                                <label class="form-label">Key Facts</label>
                                                <div v-for="(fact, fi) in page.keyFacts" :key="fi" class="key-fact-row">
                                                    <input class="form-control" v-model="page.keyFacts[fi]" />
                                                    <button class="btn-icon" @click="removeKeyFact(page, fi)" title="Remove">×</button>
                                                </div>
                                                <button class="btn btn-outline-secondary btn-sm" @click="addKeyFact(page)">
                                                    + Add Fact
                                                </button>
                                            </div>
                                            <div class="form-row">
                                                <label class="form-label">Content</label>
                                                <textarea class="form-control" rows="8" v-model="page.content"></textarea>
                                            </div>

                                            <div class="d-flex justify-content-between align-items-center mt-3">
                                                <small class="text-muted" v-if="page.updatedAt">
                                                    Last updated {{ formatDate(page.updatedAt) }}
                                                </small>
                                                <div class="d-flex gap-2">
                                                    <button
                                                        class="btn btn-outline-danger btn-sm"
                                                        :disabled="page._saving || page._deleting"
                                                        @click="deletePage(row, page)"
                                                    >{{ page._deleting ? 'Removing…' : 'Remove' }}</button>
                                                    <button
                                                        class="btn btn-primary btn-sm"
                                                        :disabled="page._saving || page._deleting"
                                                        @click="savePage(page)"
                                                    >{{ page._saving ? 'Saving…' : 'Save Edits' }}</button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <!-- Skipped links for this module — explained, not hidden -->
                                    <div v-if="group.skipped.length" class="skipped-block">
                                        <div class="skipped-heading">
                                            Couldn't be read
                                            <span class="text-muted fw-normal">
                                                — still tappable in the app, but the chatbot can't
                                                answer from the content
                                            </span>
                                        </div>
                                        <ul class="skipped-list">
                                            <li v-for="page in group.skipped" :key="page.id">
                                                <span class="skipped-title">{{ page.title || page.url }}</span>
                                                <span class="skipped-reason">{{ page.skipReason }}</span>
                                                <span class="skipped-path small text-muted d-block">{{ page.linkPath }}</span>
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </template>
                    </div>
                </div>
            </div>

            <!-- Pagination -->
            <div v-if="total > limit" class="d-flex justify-content-between align-items-center mt-3">
                <button
                    class="btn btn-outline-secondary btn-sm"
                    :disabled="offset === 0 || listLoading"
                    @click="changePage(-1)"
                >← Previous</button>
                <span class="text-muted small">
                    {{ offset + 1 }}–{{ Math.min(offset + limit, total) }} of {{ total }}
                </span>
                <button
                    class="btn btn-outline-secondary btn-sm"
                    :disabled="offset + limit >= total || listLoading"
                    @click="changePage(1)"
                >Next →</button>
            </div>
        </div>
    </div>
</template>

<script>
    // Below this many dashboards a search box is clutter; above it, essential.
    const SEARCH_THRESHOLD = 8;
    const SEARCH_DEBOUNCE_MS = 300;
    // Linked pages are only re-read when an admin asks (dashboard knowledge is
    // event-driven, with no crawl schedule), so we flag anything old enough that
    // the upstream page has plausibly changed.
    const STALE_AFTER_DAYS = 120;
    // How long to poll for a re-ingest to land. A dashboard with many links takes
    // a while; the request returns 202 immediately.
    const REINGEST_POLL_MS = 3000;
    const REINGEST_MAX_POLLS = 40;
    // Feature pills shown on a collapsed card before collapsing into "+N more".
    // Enough to characterize a dashboard without wrapping the header.
    const MODULE_PILL_LIMIT = 6;

    export default {
        name: 'DashboardKnowledgePanel',
        data() {
            return {
                SEARCH_THRESHOLD,
                MODULE_PILL_LIMIT,
                expanded: true,
                listLoading: true,
                listError: '',
                notMigrated: false,
                ingestingAll: false,
                rows: [],
                total: 0,
                limit: 10,
                offset: 0,
                search: '',
                searchDebounce: null,
                openDashboards: {},
                openPages: {},
            };
        },
        mounted() {
            this.loadList();
        },
        beforeDestroy() {
            if (this.searchDebounce) clearTimeout(this.searchDebounce);
            this.rows.forEach((r) => {
                if (r._pollHandle) clearTimeout(r._pollHandle);
            });
        },
        methods: {
            // ── List ────────────────────────────────────────────────────────
            async loadList() {
                this.listLoading = true;
                this.listError = '';
                this.notMigrated = false;
                try {
                    const response = await this.$api.get('/chatbot-builder/dashboard-knowledge', {
                        params: { search: this.search, limit: this.limit, offset: this.offset },
                    });
                    const data = (response.data && response.data.data) || {};
                    this.total = data.total || 0;
                    // Preserve any already-loaded detail so a re-list (e.g. after
                    // search) doesn't collapse what the admin was editing.
                    const priorDetail = {};
                    this.rows.forEach((r) => {
                        if (r._detail) priorDetail[r.dashboardId] = r._detail;
                    });
                    this.rows = (data.rows || []).map((r) => ({
                        ...r,
                        _detail: priorDetail[r.dashboardId] || null,
                        _loading: false,
                        _error: '',
                        _savingDescription: false,
                        _reingesting: false,
                        _pollHandle: null,
                        _polls: 0,
                    }));

                    // Most companies run a single shared dashboard, so collapsing
                    // the only card behind a click is pure friction — open it.
                    if (this.rows.length === 1) {
                        const only = this.rows[0];
                        this.$set(this.openDashboards, only.dashboardId, true);
                        if (!only._detail) await this.loadDetail(only);
                    }
                } catch (e) {
                    const code = e && e.response && e.response.data && e.response.data.code;
                    if (code === 'NO_CHATBOT') {
                        // A company with no chatbot configured yet is a normal
                        // state, not an error worth a red banner.
                        this.rows = [];
                        this.total = 0;
                    } else if (code === 'NOT_MIGRATED') {
                        // Deployment step outstanding, not a user error — say so
                        // plainly so nobody debugs the feature instead of the deploy.
                        this.rows = [];
                        this.total = 0;
                        this.notMigrated = true;
                    } else {
                        this.listError = this.errorText(e, 'Could not load dashboard knowledge.');
                    }
                } finally {
                    this.listLoading = false;
                }
            },

            async reingestAll() {
                this.ingestingAll = true;
                try {
                    const response = await this.$api.post(
                        '/chatbot-builder/dashboard-knowledge/reingest-all'
                    );
                    const message =
                        (response.data && response.data.message) ||
                        'Ingestion started.';
                    this.$toasted.success(message);
                    // Give the first dashboards a head start, then refresh the list
                    // so counts and timestamps begin to move. Full completion still
                    // trails in the background; the admin can refresh again.
                    setTimeout(() => this.loadList(), 4000);
                } catch (e) {
                    this.$toasted.error(this.errorText(e, 'Could not start ingestion.'));
                } finally {
                    this.ingestingAll = false;
                }
            },

            onSearchInput() {
                if (this.searchDebounce) clearTimeout(this.searchDebounce);
                this.searchDebounce = setTimeout(() => {
                    this.offset = 0;
                    this.loadList();
                }, SEARCH_DEBOUNCE_MS);
            },

            clearSearch() {
                this.search = '';
                this.offset = 0;
                this.loadList();
            },

            changePage(direction) {
                const next = this.offset + direction * this.limit;
                if (next < 0 || next >= this.total) return;
                this.offset = next;
                this.loadList();
            },

            // ── Detail ──────────────────────────────────────────────────────
            async toggleDashboard(row) {
                const open = !this.openDashboards[row.dashboardId];
                this.$set(this.openDashboards, row.dashboardId, open);
                if (open && !row._detail) await this.loadDetail(row);
            },

            async loadDetail(row) {
                this.$set(row, '_loading', true);
                this.$set(row, '_error', '');
                try {
                    const response = await this.$api.get(
                        `/chatbot-builder/dashboard-knowledge/${row.dashboardId}`
                    );
                    const detail = (response.data && response.data.data) || {};
                    detail.pages = (detail.pages || []).map((p) => ({
                        ...p,
                        keyFacts: Array.isArray(p.keyFacts) ? p.keyFacts : [],
                        _saving: false,
                        _deleting: false,
                    }));
                    this.$set(row, '_detail', detail);
                } catch (e) {
                    this.$set(row, '_error', this.errorText(e, 'Could not load this dashboard.'));
                } finally {
                    this.$set(row, '_loading', false);
                }
            },

            togglePage(pageId) {
                this.$set(this.openPages, pageId, !this.openPages[pageId]);
            },

            /**
             * Group a dashboard's linked resources under the feature (module)
             * whose tiles they hang off.
             *
             * Grouped rather than flat because that mirrors how the chatbot scopes
             * retrieval: a member whose dashboard lacks a module never sees its
             * links. Seeing the same grouping here makes the scoping legible.
             *
             * Every module on the dashboard gets a group even with no links, so an
             * admin can tell "nothing linked here" apart from "this feature isn't
             * on the dashboard".
             */
            moduleGroups(row) {
                if (!row._detail) return [];

                const groups = new Map();
                const ensure = (name) => {
                    if (!groups.has(name)) groups.set(name, { module: name, indexed: [], skipped: [] });
                    return groups.get(name);
                };

                (row._detail.modules || []).forEach((m) => ensure(m));

                for (const page of row._detail.pages || []) {
                    // A page whose module didn't survive normalization still has to
                    // appear somewhere, or an admin would never find it to fix.
                    const group = ensure(page.module || 'Other');
                    // A page can carry BOTH content and a skip reason: the last
                    // refresh failed but we kept the previous copy, which is still
                    // what the chatbot answers from. It belongs with the working
                    // resources (flagged), not in the "couldn't be read" list —
                    // filing it as skipped would tell the admin the content is gone
                    // when it isn't.
                    if (page.skipReason && !page.chunksIndexed) group.skipped.push(page);
                    else group.indexed.push(page);
                }

                // Features carrying content first — that's what an admin came to
                // review. 'Other' last, since it's the catch-all.
                return Array.from(groups.values()).sort((a, b) => {
                    if (a.module === 'Other') return 1;
                    if (b.module === 'Other') return -1;
                    const aHas = a.indexed.length + a.skipped.length > 0;
                    const bHas = b.indexed.length + b.skipped.length > 0;
                    if (aHas !== bHas) return aHas ? -1 : 1;
                    return a.module.localeCompare(b.module);
                });
            },

            // ── Mutations ───────────────────────────────────────────────────
            async saveDescription(row) {
                this.$set(row, '_savingDescription', true);
                try {
                    await this.$api.put(`/chatbot-builder/dashboard-knowledge/${row._detail.id}`, {
                        description: row._detail.description,
                    });
                    row.description = row._detail.description;
                    this.$toasted.success('Description saved and re-indexed.');
                } catch (e) {
                    this.$toasted.error(this.errorText(e, 'Could not save the description.'));
                } finally {
                    this.$set(row, '_savingDescription', false);
                }
            },

            async savePage(page) {
                this.$set(page, '_saving', true);
                try {
                    const response = await this.$api.put(
                        `/chatbot-builder/dashboard-knowledge/pages/${page.id}`,
                        {
                            title:    page.title,
                            topic:    page.topic,
                            summary:  page.summary,
                            keyFacts: page.keyFacts,
                            content:  page.content,
                        }
                    );
                    const saved = (response.data && response.data.data) || {};
                    page.chunksIndexed = saved.chunksIndexed || page.chunksIndexed;
                    page.updatedAt = saved.updatedAt || page.updatedAt;
                    this.$toasted.success('Page saved and re-indexed.');
                } catch (e) {
                    this.$toasted.error(this.errorText(e, 'Could not save this page.'));
                } finally {
                    this.$set(page, '_saving', false);
                }
            },

            async deletePage(row, page) {
                this.$set(page, '_deleting', true);
                try {
                    await this.$api.delete(`/chatbot-builder/dashboard-knowledge/pages/${page.id}`);
                    row._detail.pages = row._detail.pages.filter((p) => p.id !== page.id);
                    // The link is still on the dashboard — be explicit, or an admin
                    // will wonder why it reappears after the next re-ingest.
                    this.$toasted.success(
                        'Removed from the chatbot. The link stays on the dashboard and will return if you re-ingest.'
                    );
                } catch (e) {
                    this.$toasted.error(this.errorText(e, 'Could not remove this page.'));
                    this.$set(page, '_deleting', false);
                }
            },

            async reingest(row) {
                this.$set(row, '_reingesting', true);
                this.$set(row, '_polls', 0);
                const priorIngestedAt = row.lastIngestedAt;
                try {
                    await this.$api.post(
                        `/chatbot-builder/dashboard-knowledge/${row.dashboardId}/reingest`
                    );
                    this.$toasted.success('Re-reading this dashboard — this can take a minute.');
                    this.pollForReingest(row, priorIngestedAt);
                } catch (e) {
                    this.$toasted.error(this.errorText(e, 'Could not start re-ingestion.'));
                    this.$set(row, '_reingesting', false);
                }
            },

            /**
             * Ingestion runs in the background, so poll until `lastIngestedAt`
             * moves past what it was when we started. Bounded, so a failed run
             * doesn't leave the button spinning forever.
             */
            pollForReingest(row, priorIngestedAt) {
                const handle = setTimeout(async () => {
                    row._polls += 1;
                    try {
                        const response = await this.$api.get(
                            `/chatbot-builder/dashboard-knowledge/${row.dashboardId}`
                        );
                        const detail = (response.data && response.data.data) || {};
                        const moved =
                            detail.lastIngestedAt &&
                            String(detail.lastIngestedAt) !== String(priorIngestedAt);

                        if (moved) {
                            this.$set(row, '_reingesting', false);
                            this.$set(row, '_detail', null);
                            row.lastIngestedAt = detail.lastIngestedAt;
                            row.linksTotal     = detail.linksTotal;
                            row.linksIngested  = detail.linksIngested;
                            row.linksSkipped   = detail.linksSkipped;
                            row.chunksIndexed  = detail.chunksIndexed;
                            row.description    = detail.description;
                            if (this.openDashboards[row.dashboardId]) await this.loadDetail(row);
                            this.$toasted.success('Dashboard knowledge updated.');
                            return;
                        }
                    } catch (e) {
                        // Detail may 404 briefly on a first-ever ingest — keep polling.
                    }

                    if (row._polls >= REINGEST_MAX_POLLS) {
                        this.$set(row, '_reingesting', false);
                        this.$toasted.error(
                            'Re-ingestion is taking longer than expected. Refresh the page to check on it.'
                        );
                        return;
                    }
                    this.pollForReingest(row, priorIngestedAt);
                }, REINGEST_POLL_MS);
                this.$set(row, '_pollHandle', handle);
            },

            addKeyFact(page) {
                if (!Array.isArray(page.keyFacts)) this.$set(page, 'keyFacts', []);
                page.keyFacts.push('');
            },
            removeKeyFact(page, index) {
                page.keyFacts.splice(index, 1);
            },

            // ── Display helpers ─────────────────────────────────────────────
            visibleModules(modules) {
                return (modules || []).slice(0, MODULE_PILL_LIMIT);
            },

            isStale(row) {
                if (!row.lastIngestedAt || !row.linksIngested) return false;
                const then = new Date(row.lastIngestedAt).getTime();
                if (isNaN(then)) return false;
                return Date.now() - then > STALE_AFTER_DAYS * 24 * 60 * 60 * 1000;
            },

            formatDate(value) {
                if (!value) return '';
                const d = new Date(value);
                return isNaN(d.getTime()) ? '' : d.toLocaleString();
            },

            errorText(e, fallback) {
                return (
                    (e && e.response && e.response.data && e.response.data.message) ||
                    (e && e.message) ||
                    fallback
                );
            },
        },
    };
</script>

<style scoped>
    .dashboard-kb-panel .section-heading {
        cursor: pointer;
        font-size: 1rem;
        font-weight: 600;
        margin: 0;
        border-bottom: 1px solid #e9ecef;
    }
    .toggle-icon {
        font-size: 1.25rem;
        line-height: 1;
        color: #6c757d;
    }

    .panel-intro {
        flex: 1 1 320px;
        min-width: 0;
    }

    .kb-search-row {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        flex-wrap: wrap;
    }
    .kb-search-input-wrapper {
        position: relative;
        flex: 1 1 320px;
        min-width: 0;
    }
    .kb-search-input {
        padding-right: 2rem;
    }
    .kb-search-clear {
        position: absolute;
        right: 0.5rem;
        top: 50%;
        transform: translateY(-50%);
        border: 0;
        background: transparent;
        font-size: 1.25rem;
        line-height: 1;
        color: #6c757d;
        cursor: pointer;
    }

    .accordion-header,
    .page-header {
        width: 100%;
        display: flex;
        align-items: center;
        gap: 0.75rem;
        padding: 0.75rem 1rem;
        background: #fff;
        border: 0;
        text-align: left;
        cursor: pointer;
    }
    .accordion-header:hover,
    .page-header:hover {
        background: #f8f9fa;
    }
    .accordion-toggle {
        font-size: 1.1rem;
        line-height: 1;
        color: #6c757d;
        flex: 0 0 auto;
    }
    .accordion-title {
        flex: 1 1 auto;
        min-width: 0;
        overflow-wrap: anywhere;
    }
    .accordion-url {
        overflow-wrap: anywhere;
    }

    .kind-pill,
    .chunks-pill,
    .stale-pill {
        flex: 0 0 auto;
        font-size: 0.72rem;
        padding: 0.15rem 0.5rem;
        border-radius: 999px;
        white-space: nowrap;
    }
    .kind-pill   { background: #e7f1ff; color: #0d6efd; text-transform: uppercase; }
    .chunks-pill { background: #f1f3f5; color: #495057; }
    .stale-pill  { background: #fff3cd; color: #856404; }

    /* Feature pills on the collapsed card */
    .module-pills {
        display: flex;
        flex-wrap: wrap;
        gap: 0.3rem;
        margin: 0.35rem 0 0.15rem;
    }
    .module-pill {
        font-size: 0.72rem;
        padding: 0.12rem 0.5rem;
        border-radius: 999px;
        background: #e9f7ef;
        color: #146c43;
        white-space: nowrap;
    }
    .module-pill-more {
        background: #f1f3f5;
        color: #495057;
        cursor: help;
    }

    /* Module groups — one block per feature on the dashboard */
    .section-subheading {
        font-size: 0.9rem;
        font-weight: 600;
        margin: 1.25rem 0 0.75rem;
    }

    .module-group {
        overflow: hidden;
    }
    .module-group-header {
        display: flex;
        align-items: baseline;
        justify-content: space-between;
        gap: 0.75rem;
        flex-wrap: wrap;
        padding: 0.6rem 0.9rem;
        background: #f8f9fa;
        border-bottom: 1px solid #e9ecef;
    }
    .module-name {
        font-size: 0.88rem;
        font-weight: 600;
        color: #212529;
    }
    .module-counts {
        white-space: nowrap;
    }
    .module-group-body {
        padding: 0.75rem 0.9rem;
    }
    .module-group-body .page-item:last-child {
        margin-bottom: 0 !important;
    }

    .form-row {
        margin-bottom: 0.9rem;
    }
    .form-label {
        font-size: 0.82rem;
        font-weight: 600;
        margin-bottom: 0.3rem;
    }

    .tile-tree {
        background: #f8f9fa;
        border: 1px solid #e9ecef;
        border-radius: 0.375rem;
        padding: 0.75rem;
        font-size: 0.8rem;
        line-height: 1.5;
        margin: 0;
        max-height: 260px;
        overflow: auto;
        white-space: pre;
    }

    .key-fact-row {
        display: flex;
        align-items: center;
        gap: 0.4rem;
        margin-bottom: 0.35rem;
    }
    .btn-icon {
        border: 0;
        background: transparent;
        font-size: 1.15rem;
        line-height: 1;
        color: #dc3545;
        cursor: pointer;
        flex: 0 0 auto;
    }

    .skipped-block {
        margin-top: 0.75rem;
        padding-top: 0.75rem;
        border-top: 1px dashed #e9ecef;
    }
    .skipped-heading {
        font-size: 0.8rem;
        font-weight: 600;
        color: #856404;
        margin-bottom: 0.4rem;
    }

    .skipped-list {
        list-style: none;
        padding: 0;
        margin: 0;
    }
    .skipped-list li {
        padding: 0.5rem 0.75rem;
        border: 1px dashed #e9ecef;
        border-radius: 0.375rem;
        margin-bottom: 0.35rem;
        background: #fcfcfd;
    }
    .skipped-title {
        font-size: 0.85rem;
        font-weight: 600;
        margin-right: 0.5rem;
        overflow-wrap: anywhere;
    }
    .skipped-reason {
        font-size: 0.75rem;
        color: #856404;
        background: #fff3cd;
        padding: 0.1rem 0.45rem;
        border-radius: 999px;
        white-space: nowrap;
    }
    .skipped-path {
        overflow-wrap: anywhere;
    }
</style>
